/**
 * The event's play world layer as viewers get it (docs/plans/2026-10-20-play-world-layer-design.md): sponsor
 * placements, course points and hotspots the world draws over its terrain. It arrives in the viewer payload
 * (`world`) and, once the room metadata's `world_version` moves past it, from `GET /public/events/:id/world`.
 *
 * Read defensively: an element that can't be drawn honestly (no anchor, no name, an unknown kind) is left out rather
 * than drawn wrong, an action it can't carry out (a camera with no identity, a link that isn't https) falls back to
 * its card, and a look it can't draw (an unknown prop, a prop on anything but a sponsor) falls back to its marker.
 * Text stays text: labels and notes are drawn by React, never as HTML.
 */

import { apiUrl } from '@/lib/apiBase'
import type { ViewerSponsor } from './viewerPayload'

export type WorldElementKind = 'sponsor' | 'point' | 'hotspot'
export type WorldPointType = 'aid' | 'water' | 'cutoff' | 'climb' | 'landmark'
export type WorldElementAction = 'card' | 'camera' | 'link'
/** How the world draws an element: a DOM marker, or one of our own 3D props with the marker floating over it. */
export type WorldElementLook = 'marker' | 'prop'
/**
 * Our fixed library of sponsor props (`components/terrain-v2/scene/Props.tsx`): never an uploaded model. Every one
 * stands in the world but the airship, which hovers over it.
 */
export type WorldProp = 'arch' | 'board' | 'flag_line' | 'inflatable' | 'podium' | 'airship'

/** A public element: visible, its sponsor's campaign on, its camera public. */
export interface WorldElement {
  id: string
  kind: WorldElementKind
  /** Set for a `point`, null otherwise. */
  point_type: WorldPointType | null
  /** Anchored on a course: its id and the distance along it. Null for an element anchored at `lat`/`lng`. */
  route_id: string | null
  km: number | null
  lat: number | null
  lng: number | null
  /** At most 40 characters; a sponsor's is its sponsor name unless the host gave it another. */
  label: string
  note: string | null
  action: WorldElementAction
  /** A camera action's camera, as the world's camera pins know it (its participant identity). */
  camera_identity: string | null
  /** A link action's https address. */
  url: string | null
  /** A sponsor's campaign (`Campaign#viewer_payload`): its artwork always comes from there. */
  sponsor: ViewerSponsor | null
  /** `prop` only for a sponsor with a prop the world knows; anything else is drawn as a marker. */
  look: WorldElementLook
  /** Set exactly when `look` is `prop`. */
  prop: WorldProp | null
  /**
   * Which way a prop at `lat`/`lng` faces: compass degrees, 0 north, clockwise. Null for an element on a course (it
   * faces along its course) and for a point with no heading.
   */
  heading_deg: number | null
}

export interface WorldLayerPayload {
  /** The event's `world_layer_version`: up by one with every change to the layer. */
  version: number
  elements: WorldElement[]
}

export interface WorldLayerResponse {
  data: WorldLayerPayload
}

/** The layer before anything is placed, or from an API that sends none. */
export const EMPTY_WORLD_LAYER: WorldLayerPayload = Object.freeze({ version: 0, elements: [] }) as WorldLayerPayload

const KINDS: readonly WorldElementKind[] = ['sponsor', 'point', 'hotspot']
const POINT_TYPES: readonly WorldPointType[] = ['aid', 'water', 'cutoff', 'climb', 'landmark']
export const WORLD_PROPS: readonly WorldProp[] = ['arch', 'board', 'flag_line', 'inflatable', 'podium', 'airship']

type Raw = Record<string, unknown>

const isRecord = (value: unknown): value is Raw => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null)

/** An id the API sends as a uuid string; a number is read as its digits. */
const idOf = (value: unknown): string | null => (typeof value === 'number' && Number.isFinite(value) ? String(value) : text(value))

/** Rails sends decimals (km, lat, lng) as strings: read either. */
function numberOf(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** An address a viewer may be sent to: http or https only, never `javascript:` or a relative path. */
function webAddress(value: unknown, { httpsOnly = false } = {}): string | null {
  const raw = text(value)
  if (!raw) return null
  try {
    const { protocol } = new URL(raw)
    return protocol === 'https:' || (!httpsOnly && protocol === 'http:') ? raw : null
  } catch {
    return null
  }
}

/** Artwork the page may load: an http(s) address, or a path on this site (`/brands/…`). */
function imageAddress(value: unknown): string | null {
  const raw = text(value)
  if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw
  return webAddress(raw)
}

function sponsorOf(value: unknown): ViewerSponsor | null {
  if (!isRecord(value)) return null
  const campaignId = idOf(value.campaign_id)
  const sponsorName = text(value.sponsor_name)
  if (!campaignId || !sponsorName) return null
  return {
    campaign_id: campaignId,
    sponsor_name: sponsorName,
    // Left empty when it isn't a web address: the card then offers no Visit.
    click_through_url: webAddress(value.click_through_url) ?? '',
    logo_url: imageAddress(value.logo_url),
    banner_mobile_url: imageAddress(value.banner_mobile_url),
    banner_pillar_url: imageAddress(value.banner_pillar_url),
    alt_text: text(value.alt_text) ?? sponsorName,
  }
}

/** One public element, or null when it can't be drawn. */
export function parseWorldElement(value: unknown): WorldElement | null {
  if (!isRecord(value)) return null
  const id = idOf(value.id)
  const kind = KINDS.find((candidate) => candidate === value.kind)
  if (!id || !kind) return null

  const pointType = POINT_TYPES.find((candidate) => candidate === value.point_type) ?? null
  if (kind === 'point' && !pointType) return null
  const sponsor = kind === 'sponsor' ? sponsorOf(value.sponsor) : null
  if (kind === 'sponsor' && !sponsor) return null

  // Exactly one anchor: a course and a distance along it, else a point on the map.
  const routeId = idOf(value.route_id)
  const km = numberOf(value.km)
  const lat = numberOf(value.lat)
  const lng = numberOf(value.lng)
  const onCourse = routeId !== null && km !== null && km >= 0
  const atPoint = lat !== null && lng !== null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
  if (!onCourse && !atPoint) return null

  const label = text(value.label) ?? sponsor?.sponsor_name ?? null
  if (!label) return null

  const cameraIdentity = text(value.camera_identity)
  const url = webAddress(value.url, { httpsOnly: true })
  const action: WorldElementAction =
    value.action === 'camera' && cameraIdentity ? 'camera' : value.action === 'link' && url ? 'link' : 'card'

  // A prop only for a sponsor that asks for one the world knows: an unknown look or prop is drawn as a marker.
  const prop = kind === 'sponsor' && value.look === 'prop' ? (WORLD_PROPS.find((candidate) => candidate === value.prop) ?? null) : null
  // Compass degrees; a course anchor faces along its course, so only a point keeps one.
  const heading = onCourse ? null : numberOf(value.heading_deg)

  return {
    id,
    kind,
    point_type: kind === 'point' ? pointType : null,
    route_id: onCourse ? routeId : null,
    km: onCourse ? km : null,
    lat: onCourse ? null : lat,
    lng: onCourse ? null : lng,
    label,
    note: text(value.note),
    action,
    camera_identity: action === 'camera' ? cameraIdentity : null,
    url: action === 'link' ? url : null,
    sponsor,
    look: prop ? 'prop' : 'marker',
    prop,
    heading_deg: heading !== null && heading >= 0 && heading <= 360 ? heading % 360 : null,
  }
}

/** The layer, its malformed elements left out; null when it has no version to compare (not a layer at all). */
export function parseWorldLayer(value: unknown): WorldLayerPayload | null {
  if (!isRecord(value)) return null
  const version = numberOf(value.version)
  if (version === null || version < 0 || !Number.isInteger(version)) return null
  const elements = Array.isArray(value.elements)
    ? value.elements.map(parseWorldElement).filter((element): element is WorldElement => element !== null)
    : []
  return { version, elements }
}

/** `GET /public/events/:id/world`. Rejects on an error status or a body that isn't a layer. */
export async function loadWorldLayer(eventId: string, signal?: AbortSignal): Promise<WorldLayerPayload> {
  const res = await fetch(apiUrl(`public/events/${encodeURIComponent(eventId)}/world`), {
    credentials: 'omit',
    // Asked for because the version moved: a copy the browser kept from before would be the old layer.
    cache: 'no-cache',
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!res.ok) throw new Error(`Failed to load the world layer (${res.status} ${res.statusText})`)
  const body = (await res.json()) as Partial<WorldLayerResponse> | null
  const layer = parseWorldLayer(body?.data)
  if (!layer) throw new Error('The world layer response had no layer')
  return layer
}
